import {
  ROTULO_ETAPA,
  tagProduto,
  type AtendenteDTO,
  type ConversaResumoDTO,
} from '@viniko/shared';
import {
  Ban,
  BellOff,
  Camera,
  Clock,
  FileText,
  Mic,
  Pin,
  Search,
  SlidersHorizontal,
  Video,
  X,
} from 'lucide-react';
import { ChevronDown } from 'lucide-react';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { AvatarContato } from '../../componentes/ui';
import {
  useAtendentes,
  useContagem,
  useJanelaAberta,
  useLimitesFila,
  useListaConversas,
} from '../../lib/consultas';
import { useCorAtendente } from '../../lib/tema';
import { duracaoCurta, minutosDesde, nivelEspera, quandoCurto } from '../../lib/tempo';
import { useAgora } from '../../lib/useAgora';
import type { FiltrosLista } from './Conversas';
import { SimuladorCliente } from './SimuladorCliente';
import { semFormatacao } from '../../lib/formatacao';

interface Props {
  eu: AtendenteDTO;
  filtros: FiltrosLista;
  busca: string;
  mudar: (f: Partial<FiltrosLista>) => void;
}

export function ListaConversas({ eu, filtros, busca, mudar }: Props) {
  const agora = useAgora();
  const contagem = useContagem().data;
  const atendentes = useAtendentes().data ?? [];
  const [mostrarFiltro, setMostrarFiltro] = useState(false);
  const [produto, setProduto] = useState<string | null>(null);
  const lista = useListaConversas({
    aba: filtros.aba,
    vez: filtros.aba === 'minhas' ? filtros.vez : undefined,
    busca: busca || undefined,
    atendenteId: filtros.atendenteId,
  });

  const limites = useLimitesFila();
  const filaMin = minutosDesde(contagem?.filaMaisAntiga ?? null, agora);
  const abas: { k: FiltrosLista['aba']; rotulo: string; n?: number }[] = [
    { k: 'minhas', rotulo: 'Minhas', n: contagem?.minhas },
    { k: 'fila', rotulo: 'Fila', n: contagem?.fila },
    { k: 'equipe', rotulo: 'Equipe', n: contagem?.equipe },
  ];

  const emBusca = busca.length > 0;
  const produtos = produtosDaLista(lista.data ?? []);
  const produtoAtivo = produto && produtos.some((p) => p.nome === produto) ? produto : null;
  const conversas = produtoAtivo
    ? (lista.data ?? []).filter((c) => c.produtos.some((p) => p.nome === produtoAtivo))
    : lista.data;
  const filtroAtivo = atendentes.find((a) => a.id === filtros.atendenteId);
  const responder = contagem?.minhasResponder ?? 0;
  const aguardando = (contagem?.minhas ?? 0) - responder;

  return (
    <section className="lista" aria-label="Conversas">
      <header className="lista-topo">
        <div className="lista-titulo">
          <h1>Conversas</h1>
          <div className="lista-ferramentas">
            {import.meta.env.DEV && <SimuladorCliente />}
            <button
              className={`icone-btn sem-borda ${filtroAtivo ? 'ativo' : ''}`}
              title="Filtros"
              aria-label="Filtros"
              aria-expanded={mostrarFiltro}
              onClick={() => setMostrarFiltro((v) => !v)}
            >
              <SlidersHorizontal />
            </button>
          </div>
        </div>

        <label className="busca">
          <Search aria-hidden />
          <input
            id="busca-conversas"
            type="search"
            placeholder="Buscar por nome ou telefone"
            value={filtros.busca}
            onChange={(e) => mudar({ busca: e.target.value })}
          />
          {filtros.busca && (
            <button
              className="icone-btn sem-borda"
              aria-label="Limpar busca"
              onClick={() => mudar({ busca: '' })}
            >
              <X />
            </button>
          )}
        </label>

        {produtos.length > 0 && (
          <div className="filtro-produtos" role="group" aria-label="Filtrar por produto">
            {produtos.map((p) => (
              <button
                key={p.nome}
                className="item-tag produto"
                style={{ '--etq': p.cor } as CSSProperties}
                aria-pressed={produtoAtivo === p.nome}
                onClick={() => setProduto(produtoAtivo === p.nome ? null : p.nome)}
              >
                {p.nome}
              </button>
            ))}
          </div>
        )}

        {mostrarFiltro && (
          <div className="filtro-painel entra">
            <label className="campo">
              <span>Atendente</span>
              <select
                id="filtro-atendente"
                className="input"
                value={filtros.atendenteId ?? ''}
                onChange={(e) => mudar({ atendenteId: e.target.value || undefined })}
              >
                <option value="">Todos</option>
                {atendentes
                  .filter((a) => a.ativo)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nomeExibicao}
                    </option>
                  ))}
              </select>
            </label>
            <small>
              Filtros por etiqueta, origem e etapa do pedido chegam junto com Contatos e Pedidos.
            </small>
          </div>
        )}
        {filtroAtivo && (
          <button className="filtro-chip" onClick={() => mudar({ atendenteId: undefined })}>
            Atendente: {filtroAtivo.nomeExibicao} <X aria-label="Remover filtro" />
          </button>
        )}
      </header>

      {emBusca ? (
        <p className="busca-aviso">Resultados em todas as conversas, inclusive finalizadas.</p>
      ) : (
        <>
          <nav className="abas" role="tablist" aria-label="Filas de conversas">
            {abas.map((a) => (
              <button
                key={a.k}
                role="tab"
                aria-selected={filtros.aba === a.k}
                className="aba"
                onClick={() => mudar({ aba: a.k, vez: undefined })}
              >
                {a.rotulo}
                <span className="aba-n">{a.n ?? '–'}</span>
              </button>
            ))}
          </nav>

          {filtros.aba === 'minhas' && (
            <div className="vez" role="group" aria-label="De quem é a vez">
              <button
                aria-pressed={filtros.vez === undefined}
                onClick={() => mudar({ vez: undefined })}
              >
                Todas
              </button>
              <button
                aria-pressed={filtros.vez === 'responder'}
                onClick={() => mudar({ vez: 'responder' })}
              >
                Responder{' '}
                <span className={responder ? 'vez-n destaque' : 'vez-n'}>{responder}</span>
              </button>
              <button
                aria-pressed={filtros.vez === 'aguardando'}
                onClick={() => mudar({ vez: 'aguardando' })}
              >
                Aguardando cliente <span className="vez-n">{aguardando}</span>
              </button>
            </div>
          )}
          {filtros.aba === 'fila' && filaMin !== null && (
            <p className={`fila-resumo ${nivelEspera(filaMin, limites)}`}>
              <Clock aria-hidden /> Mais antiga esperando há {duracaoCurta(filaMin)}
            </p>
          )}
        </>
      )}

      <div className="lista-itens">
        {lista.isError ? (
          <p className="lista-vazia">
            Não consegui carregar as conversas. {(lista.error as Error).message}
          </p>
        ) : conversas?.length === 0 ? (
          <p className="lista-vazia">
            {produtoAtivo
              ? `Nenhuma conversa com ${produtoAtivo} aqui.`
              : textoVazio(emBusca, filtros)}
          </p>
        ) : (
          conversas &&
          (filtros.aba === 'equipe' && !emBusca ? (
            <EquipeAgrupada conversas={conversas} atendentes={atendentes} eu={eu} agora={agora} />
          ) : (
            conversas.map((c) => (
              <ItemConversa key={c.id} c={c} eu={eu} agora={agora} aba={filtros.aba} />
            ))
          ))
        )}
      </div>
    </section>
  );
}

function textoVazio(emBusca: boolean, f: FiltrosLista): string {
  if (emBusca) return 'Nenhuma conversa encontrada.';
  if (f.aba === 'minhas' && f.vez === 'responder')
    return 'Tudo respondido. Nenhum cliente esperando você.';
  return {
    minhas: 'Você não está atendendo nenhuma conversa. Assuma uma da Fila.',
    fila: 'Fila vazia. Ninguém esperando atendimento.',
    equipe: 'Nenhum colega nem a IA atendendo agora.',
    ia: 'A IA não está atendendo ninguém agora.',
  }[f.aba];
}

function prefixoAutor(c: ConversaResumoDTO, eu: AtendenteDTO): string {
  const u = c.ultimaMensagem;
  if (!u) return '';
  if (u.autor === 'ia') return 'IA: ';
  if (u.autor === 'atendente')
    return c.atendente?.id === eu.id ? 'Você: ' : `${c.atendente?.nomeExibicao ?? 'Equipe'}: `;
  if (u.autor === 'celular') return 'Celular: ';
  return '';
}

const ICONE_TIPO: Partial<Record<string, ReactNode>> = {
  audio: <Mic />,
  imagem: <Camera />,
  video: <Video />,
  documento: <FileText />,
};
const ROTULO_TIPO: Partial<Record<string, string>> = {
  audio: 'Áudio',
  imagem: 'Foto',
  video: 'Vídeo',
  documento: 'Documento',
};

function ItemConversa({
  c,
  eu,
  agora,
  aba,
}: {
  c: ConversaResumoDTO;
  eu: AtendenteDTO;
  agora: number;
  aba: string;
}) {
  const cor = useCorAtendente(c.atendente?.cor);
  const esperaMin =
    c.aguardandoResposta && c.status !== 'bot'
      ? minutosDesde(c.ultimaMensagemClienteEm, agora)
      : null;
  const limites = useLimitesFila();
  const janelaAbertaAgora = useJanelaAberta();
  const nivel = nivelEspera(esperaMin, limites);
  const janelaFechada =
    c.status !== 'finalizada' &&
    !!c.ultimaMensagemClienteEm &&
    !janelaAbertaAgora(c.ultimaMensagemClienteEm, new Date(agora));
  const u = c.ultimaMensagem;
  const tags = tagsDaConversa(c);

  const mostrarDono = aba !== 'minhas' && aba !== 'agrupado';
  const dono =
    c.status === 'bot'
      ? { nome: 'IA', cor: 'var(--bot)' }
      : c.atendente
        ? { nome: c.atendente.nomeExibicao, cor: cor.texto }
        : null;
  const esperando = esperaMin !== null && nivel !== 'ok';

  const dica = [
    dono && `Com: ${dono.nome}`,
    esperando && `Esperando resposta há ${duracaoCurta(esperaMin)}`,
    c.status === 'fila' && c.motivoTransferencia && `Motivo: ${c.motivoTransferencia}`,
    c.etapaPedido && `Pedido: ${ROTULO_ETAPA[c.etapaPedido]}`,
    c.produtos.length > 0 && `Produtos: ${c.produtos.map((p) => p.nome).join(', ')}`,
    c.etiquetas.length > 0 && `Etiquetas: ${c.etiquetas.map((t) => t.nome).join(', ')}`,
    janelaFechada && 'Janela de 24h fechada: vai precisar do modelo de reabertura',
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <NavLink
      to={`/conversas/${c.id}`}
      className={`item ${c.naoLidas > 0 ? 'nao-lida' : ''}`}
      title={dica || undefined}
    >
      <span className="item-avatar">
        <AvatarContato id={c.contato.id} nome={c.contato.nome} fotoUrl={c.contato.fotoUrl} />
        {mostrarDono && dono && (
          <span
            className="dono-canto"
            style={{ background: dono.cor }}
            aria-label={`Com ${dono.nome}`}
          />
        )}
      </span>
      <span className="item-meio">
        <span className="item-linha">
          <span className="item-nome">{c.contato.nome}</span>
          {janelaFechada && <Clock className="ico-janela" aria-label="Janela de 24h fechada" />}
          {esperando ? (
            <span className={`item-hora espera ${nivel}`}>{duracaoCurta(esperaMin)}</span>
          ) : (
            <span className="item-hora">{u ? quandoCurto(u.em, new Date(agora)) : ''}</span>
          )}
        </span>
        <span className="item-linha">
          <span className="item-prev">
            {prefixoAutor(c, eu)}
            {u && ICONE_TIPO[u.tipo] && <span className="ico-tipo">{ICONE_TIPO[u.tipo]}</span>}
            {(u?.texto && semFormatacao(u.texto)) ||
              (u ? (ROTULO_TIPO[u.tipo] ?? '') : 'Sem mensagens')}
          </span>
          <span className="item-sinais">
            {c.contato.bloqueado && <Ban aria-label="Bloqueado" className="ico-bloqueado" />}
            {c.fixada && <Pin aria-label="Fixada" />}
            {c.silenciada && <BellOff aria-label="Silenciada" />}
            {c.naoLidas > 0 && <span className="badge">{c.naoLidas}</span>}
          </span>
        </span>
        {tags.length > 0 && (
          <span className="item-tags">
            {tags.slice(0, MAX_TAGS).map((t) => (
              <span
                key={t.chave}
                className={t.produto ? 'item-tag produto' : 'item-tag'}
                style={{ '--etq': t.cor } as CSSProperties}
              >
                <span className="etq-ponto" />
                {t.nome}
              </span>
            ))}
            {tags.length > MAX_TAGS && (
              <span className="item-tag mais">+{tags.length - MAX_TAGS}</span>
            )}
          </span>
        )}
      </span>
    </NavLink>
  );
}

const MAX_TAGS = 3;

/** Produtos que aparecem nas conversas carregadas, sem repetir, na ordem em que aparecem. */
function produtosDaLista(lista: ConversaResumoDTO[]) {
  const vistos = new Map<string, { nome: string; cor: string }>();
  for (const c of lista) for (const p of c.produtos) if (!vistos.has(p.nome)) vistos.set(p.nome, p);
  return [...vistos.values()];
}

/** Produtos do orçamento + etiquetas manuais, sem repetir: etiqueta com nome de produto que já virou tag some. */
function tagsDaConversa(c: ConversaResumoDTO) {
  const produtos = new Set(c.produtos.map((p) => p.nome));
  return [
    ...c.produtos.map((p) => ({ chave: 'p:' + p.nome, nome: p.nome, cor: p.cor, produto: true })),
    ...c.etiquetas
      .filter((t) => !produtos.has(tagProduto(t.nome)?.nome ?? ''))
      .map((t) => ({ chave: t.id, nome: t.nome, cor: t.cor, produto: false })),
  ];
}

const ROTULO_STATUS_AT = { online: 'online', ausente: 'ausente', offline: 'offline' } as const;

function EquipeAgrupada({
  conversas,
  atendentes,
  eu,
  agora,
}: {
  conversas: ConversaResumoDTO[];
  atendentes: AtendenteDTO[];
  eu: AtendenteDTO;
  agora: number;
}) {
  const grupos = new Map<string, ConversaResumoDTO[]>();
  for (const c of conversas) {
    const chave = c.status === 'bot' ? 'ia' : (c.atendente?.id ?? 'sem');
    grupos.set(chave, [...(grupos.get(chave) ?? []), c]);
  }
  const ordem = [...grupos.keys()].sort((a, b) => {
    if (a === 'ia') return 1;
    if (b === 'ia') return -1;
    const na = atendentes.find((x) => x.id === a)?.nomeExibicao ?? '';
    const nb = atendentes.find((x) => x.id === b)?.nomeExibicao ?? '';
    return na.localeCompare(nb, 'pt-BR');
  });

  return (
    <div className="equipe-grupos">
      {ordem.map((k) => (
        <GrupoAtendente
          key={k}
          atendente={k === 'ia' ? null : (atendentes.find((a) => a.id === k) ?? null)}
          conversas={grupos.get(k)!}
          eu={eu}
          agora={agora}
        />
      ))}
    </div>
  );
}

function GrupoAtendente({
  atendente,
  conversas,
  eu,
  agora,
}: {
  atendente: AtendenteDTO | null;
  conversas: ConversaResumoDTO[];
  eu: AtendenteDTO;
  agora: number;
}) {
  const [aberto, setAberto] = useState(true);
  const cor = useCorAtendente(atendente?.cor);
  const ia = !atendente;
  const estilo = (
    ia
      ? { '--grupo-cor': 'var(--bot)', '--grupo-fundo': 'var(--bot-soft)' }
      : { '--grupo-cor': cor.texto, '--grupo-fundo': cor.fundo }
  ) as CSSProperties;
  const esperando = conversas.filter((c) => c.aguardandoResposta && c.status !== 'bot').length;

  return (
    <section className="equipe-grupo" style={estilo}>
      <button className="grupo-topo" aria-expanded={aberto} onClick={() => setAberto((v) => !v)}>
        <span className="grupo-avatar">
          {ia ? 'IA' : atendente.nomeExibicao.slice(0, 2).toUpperCase()}
        </span>
        <span className="grupo-quem">
          <b>{ia ? 'IA' : atendente.nomeExibicao}</b>
          <small>
            {ia ? 'atendendo sozinha' : ROTULO_STATUS_AT[atendente.status]}
            {esperando > 0 && (
              <>
                {' '}
                · <span className="grupo-esperando">{esperando} esperando resposta</span>
              </>
            )}
          </small>
        </span>
        <span className="grupo-n">{conversas.length}</span>
        <ChevronDown className="grupo-seta" aria-hidden />
      </button>
      {aberto && (
        <div className="grupo-itens">
          {conversas.map((c) => (
            <ItemConversa key={c.id} c={c} eu={eu} agora={agora} aba="agrupado" />
          ))}
        </div>
      )}
    </section>
  );
}
